import { VehicleEngineConcept } from 'src/app/core/vehicle/vehicle-engine-concept.enum';

/**
 * Vehicle object property paths with respective icon id
 */
const vehicleDataIcons: { [vehiclePropertyPath: string]: string } = {
  location: 'dealer',
  'technicalData.engine.emissionStandard': 'badge-eco',
  'technicalData.engine.engineConcept': 'fuel',
  'technicalData.engine.fuelType': 'fuel',
  'technicalData.engine.enginePerformance': 'engine',
  'technicalData.engine.powerHp': 'engine',
  'technicalData.engine.cylinder': 'engine',
  'technicalData.engine.transmission': 'gear',
  'technicalData.engine.typeOfPropulsion': 'drive-type-all-wheel',
  'technicalData.engine.hybridType': 'fuel-hybrid/24',
  'technicalData.gear': 'gear',
  'technicalData.topSpeed': 'mileage',
  'technicalData.energyEfficiencyClass': 'consumption',
  'technicalData.driveCycle.standard.fuelConsumptionCombined': 'fuel',
  'technicalData.driveCycle.standard.co2EmissionCombined': 'consumption',
  'technicalData.driveCycle.standard.emissionCO2TargetValue': 'consumption',
  'technicalData.driveCycle.standard.emissionCO2AverageValue': 'consumption',
  'technicalData.driveCycle.standard.co2EmissionCombinedRange': 'consumption',
  'technicalData.driveCycle.standard.fuelConsumptionCombinedRange': 'fuel-comp',
  'technicalData.driveCycle.standard.fuelConsumptionEquivalentToBase': 'fuel',
  'technicalData.driveCycle.standard.co2EmissionFuelElectricityAllocation': 'consumption',
  'technicalData.driveCycle.standard.electricityConsumptionCombined': 'fuel-e-comp',
  'technicalData.driveCycle.standard.electricConsumptionCombinedRange': 'fuel-e-comp',
  'technicalData.driveCycle.nedc.emissionCombinedPrimaryGear': 'consumption',
  'technicalData.driveCycle.nedc.consumptionCombinedPrimaryGear': 'fuel-comp',
  'technicalData.driveCycle.nedc.consumptionWeightedPrimaryGear': 'fuel',
  'technicalData.driveCycle.nedc.consumptionWeightedSecondaryGear': 'fuel',
  'technicalData.driveCycle.nedc.consumptionElectric': 'fuel-comp',
  'technicalData.driveCycle.nedc.consumptionElectricIndividual': 'fuel-e-comp',
  'technicalData.driveCycle.nedc.emissionWeighted': 'consumption',
  'technicalData.driveCycle.nedc.emissionCombinedPrimaryGearIndividual': 'consumption',
  'technicalData.driveCycle.nedc.consumptionCombinedPrimaryGearIndividual': 'fuel-comp',
  'technicalData.driveCycle.nedc.electricRange': 'trip-type',
  'technicalData.driveCycle.nedc.electricRangeIndividual': 'trip-type',
  'technicalData.driveCycle.nedc.consumptionWeightedPrimaryGearIndividual': 'fuel-comp',
  'technicalData.driveCycle.nedc.consumptionWeightedSecondaryGearIndividual': 'fuel-e-comp',
  'technicalData.driveCycle.nedc.emissionWeightedIndividual': 'consumption',
  'technicalData.driveCycle.wltp.consumptionChargeDepleteWeightedSecondaryGearIndividual': 'fuel-e-comp',
  'technicalData.driveCycle.wltp.consumptionWeightedPrimaryGearIndividual': 'fuel-comp',
  'technicalData.driveCycle.wltp.consumptionTotalPrimaryGearIndividual': 'fuel-comp',
  'technicalData.driveCycle.wltp.rangeTotalAllElectricIndividual': 'fuel-e-comp',
  'technicalData.driveCycle.wltp.rangeTotalEquivalentAllElectricIndividual': 'fuel-e-comp',
  'technicalData.driveCycle.wltp.emissionTotalPrimaryGearIndividual': 'consumption',
  'technicalData.driveCycle.wltp.emissionWeightedPrimaryGearIndividual': 'consumption',
  'technicalData.energyEfficiencyClass,technicalData.driveCycle.nedc.emissionCombinedPrimaryGearIndividual':
    'consumption',
  'technicalData.energyEfficiencyClass,technicalData.driveCycle.nedc.emissionWeightedIndividual': 'consumption',
  'technicalData.energyEfficiencyClass,technicalData.driveCycle.wltp.emissionTotalPrimaryGearIndividual': 'consumption',
  'technicalData.energyEfficiencyClass,technicalData.driveCycle.wltp.emissionWeightedPrimaryGearIndividual':
    'consumption',
  'technicalData.acceleration': 'mileage-xs'
};

const vehicleDataIconsMonoElectric: { [vehiclePropertyPath: string]: string } = {
  ...vehicleDataIcons,
  'technicalData.engine.engineConcept': 'fuel-e',
  'technicalData.engine.fuelType': 'fuel-e',
  'technicalData.driveCycle.standard.fuelConsumptionCombined': 'fuel-e',
  'technicalData.driveCycle.standard.fuelConsumptionEquivalentToBase': 'fuel-e',
  'technicalData.driveCycle.standard.electricityConsumptionCombined': 'fuel-e-comp',
  'technicalData.driveCycle.standard.electricConsumptionCombinedRange': 'fuel-e-comp',
  'technicalData.driveCycle.nedc.consumptionWeightedPrimaryGear': 'fuel-e',
  'technicalData.driveCycle.nedc.consumptionWeightedSecondaryGear': 'fuel-e',
  'technicalData.chargeTime.chargeTimeACLow': 'fuel-e-comp',
  'technicalData.chargeTime.chargeTimeACMedium': 'fuel-e-comp',
  'technicalData.chargeTime.chargeTimeDCHigh': 'fuel-e-comp'
};
const vehicleDataIconsBiPlugin: { [vehiclePropertyPath: string]: string } = {
  ...vehicleDataIcons
};

export const MAP_KEYS = {
  default: VehicleEngineConcept.Default,
  monoElectric: VehicleEngineConcept.MonoElectric,
  biPlugin: VehicleEngineConcept.BiPlugin
};

/**
 * Map of vehicle object property paths with respective icon id
 */
const vehicleDataIconMapDefault: Map<string, string> = Object.entries(vehicleDataIcons).reduce<Map<string, string>>(
  (map, [vehicleProperty, iconName]) => map.set(vehicleProperty, iconName),
  new Map()
);
const vehicleDataIconMapMonoElectric: Map<string, string> = Object.entries(vehicleDataIconsMonoElectric).reduce<
  Map<string, string>
>((map, [vehicleProperty, iconName]) => map.set(vehicleProperty, iconName), new Map());
const vehicleDataIconMapBiPlugin: Map<string, string> = Object.entries(vehicleDataIconsBiPlugin).reduce<
  Map<string, string>
>((map, [vehicleProperty, iconName]) => map.set(vehicleProperty, iconName), new Map());

const maps: { [key: string]: Map<string, string> } = {
  [MAP_KEYS.default]: vehicleDataIconMapDefault,
  [MAP_KEYS.monoElectric]: vehicleDataIconMapMonoElectric,
  [MAP_KEYS.biPlugin]: vehicleDataIconMapBiPlugin
};

export const vehicleDataIconMap: { get(key: string, map?: string): string } = {
  get: (key: string, map?: string): string => {
    let selectedMap: Map<string, string> = maps[MAP_KEYS.default];

    if (!!map && !!maps[map]) {
      selectedMap = maps[map];
    }

    return selectedMap.get(key);
  }
};
